/**
 * ATS Report Adapters — V2 ↔ V3 conversion
 *
 * Used during the migration period when:
 *   - Old extension reads V2 data  → backend may return V3 → legacyAdapter
 *   - New extension reads V3 data  → storage may have V2  → v2ToV3Adapter
 *
 * These adapters are pure functions with zero side effects.
 */

import type {
  ATSReportV3Full,
  ATSReportV3,
  FullATSScoreV3,
  ATSReportCategories,
  ContactSectionScore,
  SummarySectionScore,
  ExperienceSectionScore,
  SkillsSectionScore,
  EducationSectionScore,
  ATSImprovement,
  Finding,
} from './ats-report';

import type { ATSReportV3Quick } from './ats-report';

// ---------------------------------------------------------------------------
// V2 types (imported inline to avoid tight coupling — these live in
// extension/background/types.ts but we re-declare the shapes here so the
// shared package doesn't depend on extension code)
// ---------------------------------------------------------------------------

/** Minimal V2 subsection shape */
interface V2Subsection {
  score: number; // 1-10
  feedback: string;
  tips?: string[];
}

/** Minimal V2 detailed score shape */
interface V2DetailedATSScore {
  overallScore: number;
  summary: string;
  completeness: {
    score: number;
    contactInfo: V2Subsection;
    detailLevel: V2Subsection;
  };
  roleAlignment: {
    score: number;
    skillsRelevance: V2Subsection;
    experienceAlignment: V2Subsection;
    educationFit: V2Subsection;
  };
  impact: {
    score: number;
    activeVoice: V2Subsection;
    quantifiedAchievements: V2Subsection;
  };
  topImprovements: string[];
  roleSpecificImprovements: string[];
}

/** Minimal V2 full score shape */
interface V2FullATSScore {
  formatScore: number;
  formatIssues: string[];
  contentScore: number;
  contentDetails: V2DetailedATSScore;
  overall: number;
  overallScore?: number;
}

// ---------------------------------------------------------------------------
// V2 → V3  (upgrade old data to new format)
// ---------------------------------------------------------------------------

/**
 * Convert a V2 DetailedATSScore to a V3 Quick report.
 * Used when we only have V2 data but want to render V3 UI.
 */
export function v2DetailedToV3Quick(v2: V2DetailedATSScore): ATSReportV3Quick {
  return {
    version: 3,
    tier: 'quick',
    overallScore: v2.overallScore,
    summary: v2.summary,
    categories: buildCategoriesFromV2(v2),
    criticalIssues: extractCriticalIssues(v2),
  };
}

/**
 * Convert a V2 DetailedATSScore to a V3 Full report.
 * Creates section scores from V2 subsections (no bullet rewrites or
 * error detection — those require re-analysis via V3 prompt).
 */
export function v2DetailedToV3Full(v2: V2DetailedATSScore): ATSReportV3Full {
  return {
    version: 3,
    tier: 'full',
    overallScore: v2.overallScore,
    summary: v2.summary,
    categories: buildCategoriesFromV2(v2),
    criticalIssues: extractCriticalIssues(v2),
    sections: {
      contact: buildContactSection(v2.completeness.contactInfo),
      summary: buildSummarySection(v2.completeness.detailLevel),
      experience: buildExperienceSection(v2.roleAlignment.experienceAlignment),
      skills: buildSkillsSection(v2.roleAlignment.skillsRelevance),
      education: buildEducationSection(v2.roleAlignment.educationFit),
    },
    errors: [], // V2 doesn't detect errors
    improvements: buildImprovements(v2),
  };
}

/**
 * Convert a complete V2 FullATSScore to V3.
 */
export function v2ToV3(v2: V2FullATSScore): FullATSScoreV3 {
  return {
    formatScore: v2.formatScore,
    formatIssues: v2.formatIssues,
    report: v2DetailedToV3Full(v2.contentDetails),
  };
}

// ---------------------------------------------------------------------------
// V3 → V2  (downgrade for backward compatibility with old extension)
// ---------------------------------------------------------------------------

/**
 * Convert a V3 report back to V2 DetailedATSScore shape.
 * Used when old extension reads data from storage that was saved by new extension.
 */
export function v3ToV2Detailed(v3: ATSReportV3): V2DetailedATSScore {
  const isFullTier = v3.tier === 'full';
  const full = isFullTier ? (v3 as ATSReportV3Full) : null;

  return {
    overallScore: v3.overallScore,
    summary: v3.summary,
    completeness: {
      score: v3.categories.completeness.score,
      contactInfo: full
        ? subsectionFromV3Section(full.sections.contact)
        : placeholderSubsection(v3.categories.completeness.score),
      detailLevel: full
        ? subsectionFromV3Section(full.sections.summary)
        : placeholderSubsection(v3.categories.completeness.score),
    },
    roleAlignment: {
      score: v3.categories.roleAlignment.score,
      skillsRelevance: full
        ? subsectionFromV3Section(full.sections.skills)
        : placeholderSubsection(v3.categories.roleAlignment.score),
      experienceAlignment: full
        ? subsectionFromV3Section(full.sections.experience)
        : placeholderSubsection(v3.categories.roleAlignment.score),
      educationFit: full
        ? subsectionFromV3Section(full.sections.education)
        : placeholderSubsection(v3.categories.roleAlignment.score),
    },
    impact: {
      score: full
        ? Math.round(
            ((full.sections.experience.score + full.sections.summary.score) / 2) * 10,
          ) / 10
        : v3.categories.impact.score,
      activeVoice: full
        ? subsectionFromV3Section(full.sections.experience)
        : placeholderSubsection(v3.categories.impact.score),
      quantifiedAchievements: full
        ? subsectionFromV3Section(full.sections.experience)
        : placeholderSubsection(v3.categories.impact.score),
    },
    topImprovements: full
      ? full.improvements.filter((i) => i.priority === 'high').map((i) => i.action)
      : v3.criticalIssues,
    roleSpecificImprovements: full
      ? full.improvements.filter((i) => i.priority === 'medium').map((i) => i.action)
      : [],
  };
}

/**
 * Convert a V3 FullATSScoreV3 back to V2 FullATSScore shape.
 */
export function v3ToV2(v3: FullATSScoreV3): V2FullATSScore {
  const detailed = v3ToV2Detailed(v3.report);
  return {
    formatScore: v3.formatScore,
    formatIssues: v3.formatIssues,
    contentScore: v3.report.overallScore,
    contentDetails: detailed,
    overall: Math.round((v3.formatScore + v3.report.overallScore) / 2),
    overallScore: Math.round((v3.formatScore + v3.report.overallScore) / 2),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildCategoriesFromV2(v2: V2DetailedATSScore): ATSReportCategories {
  return {
    completeness: {
      score: v2.completeness.score,
      label: 'Completeness',
      weight: 30,
    },
    roleAlignment: {
      score: v2.roleAlignment.score,
      label: 'Role Alignment',
      weight: 40,
    },
    impact: {
      score: v2.impact.score,
      label: 'Impact',
      weight: 30,
    },
  };
}

function extractCriticalIssues(v2: V2DetailedATSScore): string[] {
  const issues: string[] = [];

  const checkSubsection = (sub: V2Subsection, label: string) => {
    if (sub.score < 4) {
      issues.push(`${label}: ${sub.feedback}`);
    }
  };

  checkSubsection(v2.completeness.contactInfo, 'Contact Info');
  checkSubsection(v2.completeness.detailLevel, 'Detail Level');
  checkSubsection(v2.roleAlignment.skillsRelevance, 'Skills');
  checkSubsection(v2.roleAlignment.experienceAlignment, 'Experience');
  checkSubsection(v2.roleAlignment.educationFit, 'Education');
  checkSubsection(v2.impact.activeVoice, 'Active Voice');
  checkSubsection(v2.impact.quantifiedAchievements, 'Achievements');

  return issues;
}

function findingsFromTips(tips: string[] | undefined, score: number): Finding[] {
  if (!tips?.length) return [];
  return tips.map((tip) => ({
    severity: score < 4 ? 'critical' as const : score < 6 ? 'warning' as const : 'info' as const,
    message: tip,
  }));
}

function buildContactSection(sub: V2Subsection): ContactSectionScore {
  return {
    score: sub.score,
    label: 'Contact Information',
    feedback: sub.feedback,
    tips: sub.tips ?? [],
    findings: findingsFromTips(sub.tips, sub.score),
    hasEmail: sub.score >= 5,
    hasPhone: sub.score >= 5,
    hasLinkedIn: sub.score >= 7,
    hasLocation: sub.score >= 5,
  };
}

function buildSummarySection(sub: V2Subsection): SummarySectionScore {
  return {
    score: sub.score,
    label: 'Professional Summary',
    feedback: sub.feedback,
    tips: sub.tips ?? [],
    findings: findingsFromTips(sub.tips, sub.score),
    hasSummary: sub.score >= 3,
  };
}

function buildExperienceSection(sub: V2Subsection): ExperienceSectionScore {
  return {
    score: sub.score,
    label: 'Work Experience',
    feedback: sub.feedback,
    tips: sub.tips ?? [],
    findings: findingsFromTips(sub.tips, sub.score),
    roles: [], // V2 doesn't have per-role breakdown
  };
}

function buildSkillsSection(sub: V2Subsection): SkillsSectionScore {
  return {
    score: sub.score,
    label: 'Skills',
    feedback: sub.feedback,
    tips: sub.tips ?? [],
    findings: findingsFromTips(sub.tips, sub.score),
    relevantSkills: [],
    missingSkills: [],
  };
}

function buildEducationSection(sub: V2Subsection): EducationSectionScore {
  return {
    score: sub.score,
    label: 'Education',
    feedback: sub.feedback,
    tips: sub.tips ?? [],
    findings: findingsFromTips(sub.tips, sub.score),
  };
}

function buildImprovements(v2: V2DetailedATSScore): ATSImprovement[] {
  const improvements: ATSImprovement[] = [];

  for (const text of v2.topImprovements) {
    improvements.push({
      priority: 'high',
      category: 'General',
      action: text,
      reason: 'Top priority improvement identified in ATS analysis',
    });
  }

  for (const text of v2.roleSpecificImprovements) {
    improvements.push({
      priority: 'medium',
      category: 'Role-Specific',
      action: text,
      reason: 'Improvement specific to detected role alignment',
    });
  }

  return improvements;
}

function subsectionFromV3Section(
  section: { score: number; feedback: string; tips: string[] },
): V2Subsection {
  return {
    score: section.score,
    feedback: section.feedback,
    tips: section.tips,
  };
}

function placeholderSubsection(categoryScore: number): V2Subsection {
  return {
    score: categoryScore,
    feedback: 'Detailed analysis available in full report.',
    tips: [],
  };
}
